import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router';
import { motion } from 'framer-motion';
import { useMatchStore } from '../../../stores/match';
import { useAccountStore } from '../../../stores/account';
import { allHeroes } from '../../../game/data/heroes';
import { CombatCanvas } from '../../../combat/CombatCanvas';
import { OtherFightsPanel } from '../../../components/OtherFightsPanel';
import type { Card } from '../../../game/data/cards/types';
import { sfx } from '../../../audio/sfx';
import { haptics } from '../../../audio/haptics';
import { EDGES } from '../../../theme/tokens';
import { useEffectiveReducedMotion } from '../../../components/useReducedMotion';
import { useLayoutMode } from '../../../components/useLayoutMode';

// R6 — combat is now a real PIXI canvas. We resolve combat deterministically
// against the v1 engine, then play the event stream through CombatCanvas.
// Speed (1/2/4) and Skip controls live in React/Tailwind outside the canvas.
// The text result block (winner copy + damage + Continue) renders as a
// post-canvas overlay once playback completes (or is skipped).

type Speed = 1 | 2 | 4;

export function CombatPage() {
  const { matchId: urlMatchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const reducedMotion = useEffectiveReducedMotion();
  const { isPhone } = useLayoutMode();

  const storedMatchId = useMatchStore((s) => s.matchId);
  const heroId = useMatchStore((s) => s.heroId);
  const board = useMatchStore((s) => s.board);
  const opponentBoard = useMatchStore((s) => s.opponentBoard);
  const roundNumber = useMatchStore((s) => s.roundNumber);
  const health = useMatchStore((s) => s.health);
  const lastCombatResult = useMatchStore((s) => s.lastCombatResult);
  const opponents = useMatchStore((s) => s.opponents);
  const currentOpponentIdx = useMatchStore((s) => s.currentOpponentIdx);
  const asyncOpponent = useMatchStore((s) => s.asyncOpponent);
  const seed = useMatchStore((s) => s.seed);

  const runCombat = useMatchStore((s) => s.runCombat);
  const applyCombatDamage = useMatchStore((s) => s.applyCombatDamage);

  // Snapshot the boards at combat resolution time. The store mutates them
  // on continue, so we capture once for the canvas's lifetime.
  const [snapshot, setSnapshot] = useState<{
    player: Card[];
    enemy: Card[];
  } | null>(null);

  const [speed, setSpeed] = useState<Speed>(1);
  const [playbackComplete, setPlaybackComplete] = useState(false);
  const [resolving, setResolving] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const skipControllerRef = useRef<AbortController | null>(null);

  // Resolve combat once when we arrive on the page with a valid setup.
  useEffect(() => {
    if (!lastCombatResult && opponentBoard.length > 0) {
      runCombat();
    }
    setResolving(false);
  }, [lastCombatResult, opponentBoard.length, runCombat]);

  // Snapshot boards the moment we have a result. Cards are immutable
  // shop objects, but instanceIds are what the canvas keys off; clone
  // to a shallow array so a later store reset doesn't pull the rug.
  useEffect(() => {
    if (lastCombatResult && !snapshot) {
      setSnapshot({
        player: (board.filter(Boolean) as Card[]).map((c) => ({ ...c })),
        enemy: opponentBoard.map((c) => ({ ...c })),
      });
      skipControllerRef.current = new AbortController();
    }
  }, [lastCombatResult, snapshot, board, opponentBoard]);

  // Auto-skip combat if the user opted in via Settings. We fire it as soon
  // as the snapshot + controller are ready so the canvas never spins up.
  const autoSkipCombat = useAccountStore((s) => s.autoSkipCombat);
  useEffect(() => {
    if (autoSkipCombat && snapshot && skipControllerRef.current && !playbackComplete) {
      skipControllerRef.current.abort();
      setPlaybackComplete(true);
    }
  }, [autoSkipCombat, snapshot, playbackComplete]);

  // Banner sting when playback completes — victory chord for wins, descending
  // cadence for losses, click for draws. Fires once per playback transition.
  const winnerOnComplete = playbackComplete ? lastCombatResult?.winner ?? null : null;
  useEffect(() => {
    if (!winnerOnComplete) return;
    if (winnerOnComplete === 'player') {
      sfx.victory();
      haptics.finale();
    } else if (winnerOnComplete === 'enemy') {
      sfx.defeat();
      haptics.finale();
    } else {
      sfx.click();
    }
  }, [winnerOnComplete]);

  if (!urlMatchId || storedMatchId !== urlMatchId || !heroId) {
    return <Navigate to="/play" replace />;
  }

  if (opponentBoard.length === 0 && !lastCombatResult && !resolving) {
    return <Navigate to={`/game/${urlMatchId}/tavern`} replace />;
  }

  const hero = allHeroes.find((h) => h.id === heroId);
  const result = lastCombatResult;
  const winner = result?.winner;
  const damage = result?.damage ?? 0;
  const damageToHuman = winner === 'enemy' ? damage : 0;
  const healthAfter = Math.max(0, health - damageToHuman);

  function handleContinue() {
    const outcome = applyCombatDamage();
    if (outcome === 'eliminated' || outcome === 'victory') {
      navigate(`/game/${urlMatchId}/results`);
    } else {
      navigate(`/game/${urlMatchId}/tavern`);
    }
  }

  function handleSkip() {
    skipControllerRef.current?.abort();
    setPlaybackComplete(true);
  }

  const winnerCopy: Record<string, string> = {
    player: 'Crew holds the wall.',
    enemy: 'Crew gets pushed.',
    draw: 'Stalemate — neither side moves.',
  };
  const winnerTone =
    winner === 'player'
      ? 'text-glowStick'
      : winner === 'enemy'
        ? 'text-aerosolRed'
        : 'text-marker';

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[40vh] opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(230,52,42,0.18) 0%, rgba(230,52,42,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-xs overflow-hidden px-md py-xs md:gap-md md:px-lg md:py-md lg:gap-md lg:px-xl lg:py-md">
        {/* min-w-0 on the text group + a fixed-size icon link: the old text
            "← menu" was the flex item that got squeezed, wrapping to two
            lines at 390px and eating a row of the canvas's height. */}
        <header className="flex items-center justify-between gap-sm">
          <div className="flex min-w-0 flex-wrap items-baseline gap-sm">
            <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px] lg:text-[14px]">
              round {roundNumber} · combat
            </p>
            {(() => {
              if (asyncOpponent) {
                const asyncHero = allHeroes.find((h) => h.id === asyncOpponent.heroId);
                const oppFactionTone =
                  asyncHero?.faction === 'cholo' ? 'text-cholo' : 'text-emo';
                const heroName = asyncHero?.name ?? asyncOpponent.heroId;
                return (
                  <p className="flex flex-wrap items-baseline gap-xs font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[12px] lg:text-[14px]">
                    vs · <span className={oppFactionTone}>{asyncOpponent.handle}</span>
                    <span className="text-graphite">· {heroName}</span>
                    <span
                      className="rounded-sm bg-glowStick/20 px-xs py-[1px] font-data text-[9px] uppercase tracking-[0.25em] text-glowStick md:text-[10px]"
                      title="Real player snapshot — async multiplayer"
                    >
                      live
                    </span>
                  </p>
                );
              }
              const opp =
                currentOpponentIdx >= 0 ? opponents[currentOpponentIdx] : null;
              if (!opp) return null;
              const oppFactionTone =
                opp.hero.faction === 'cholo' ? 'text-cholo' : 'text-emo';
              return (
                <p className="font-data text-[10px] uppercase tracking-[0.3em] text-graphite md:text-[12px] lg:text-[14px]">
                  vs · <span className={oppFactionTone}>{opp.hero.name}</span>
                </p>
              );
            })()}
          </div>
          {isPhone ? (
            <Link
              to="/menu"
              aria-label="Back to menu"
              className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-sm border border-stucco3 bg-stucco2 font-data text-[16px] text-graphite hover:text-marker"
            >
              ←
            </Link>
          ) : (
            <Link
              to="/menu"
              className="shrink-0 font-data text-[10px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
            >
              ← menu
            </Link>
          )}
        </header>

        <div>
          {playbackComplete && winner ? (
            <motion.h1
              key={`winner-${winner}`}
              initial={{ opacity: 0, scale: 0.92, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className={`font-cholo text-[24px] leading-none md:text-[40px] lg:text-[56px] ${winnerTone}`}
            >
              {winnerCopy[winner]}
            </motion.h1>
          ) : (
            <h1 className="font-cholo text-[24px] leading-none text-bone md:text-[40px] lg:text-[56px]">
              {resolving ? 'Resolving…' : 'Live.'}
            </h1>
          )}
          {playbackComplete && result && (
            <p className="mt-xs hidden text-[14px] text-marker md:block md:text-[16px] lg:text-[18px]">
              {damageToHuman > 0 ? (
                <>
                  You take <span className="text-aerosolRed">{damageToHuman}</span>{' '}
                  damage. HP {health} →{' '}
                  <span className="text-bone">{healthAfter}</span>.
                </>
              ) : winner === 'player' ? (
                <>
                  You hit the opponent for{' '}
                  <span className="text-glowStick">{damage}</span>. No damage to
                  you.
                </>
              ) : (
                'Both boards traded out. No damage either way.'
              )}
            </p>
          )}
        </div>

        {/* Speed / Skip controls — only meaningful during playback */}
        {!playbackComplete && result && snapshot && (
          <div className="flex items-center justify-between gap-md">
            <SpeedControls speed={speed} onChange={setSpeed} />
            <button
              onClick={handleSkip}
              className="min-h-[44px] rounded-sm bg-stucco2 px-md font-data text-[11px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-stucco3 md:px-lg md:text-[14px]"
            >
              Skip ›
            </button>
          </div>
        )}

        {/* PIXI canvas — fills remaining vertical space across all viewports */}
        {result && snapshot && skipControllerRef.current ? (
          <CombatCanvas
            key={`${urlMatchId}-${roundNumber}`}
            result={result}
            playerBoard={snapshot.player}
            opponentBoard={snapshot.enemy}
            speed={speed}
            skipSignal={skipControllerRef.current.signal}
            onComplete={() => setPlaybackComplete(true)}
            seed={seed}
            reducedMotion={reducedMotion}
          />
        ) : (
          <div className="flex min-h-[200px] w-full flex-1 items-center justify-center rounded-md bg-asphalt">
            <span className="font-data text-[12px] uppercase tracking-[0.3em] text-marker">
              {resolving ? 'resolving combat…' : 'waiting for opponent…'}
            </span>
          </div>
        )}

        {/* Continue bar — inline for all viewports. */}
        <div className="flex items-center justify-between gap-sm md:gap-lg md:pt-md">
          <div className="flex items-center gap-sm md:gap-lg">
            <p className="hidden font-data text-[12px] uppercase tracking-[0.3em] text-marker md:inline lg:text-[14px]">
              {result?.events?.length ?? 0} combat events · {hero?.name}
            </p>
            {playbackComplete && (
              <button
                onClick={() => setReportOpen(true)}
                className="font-data text-[10px] uppercase tracking-[0.2em] text-marker hover:text-bone md:text-[11px] lg:text-[13px]"
              >
                Block report ›
              </button>
            )}
          </div>
          <motion.button
            onClick={handleContinue}
            disabled={!playbackComplete || !result}
            whileTap={playbackComplete && result && !reducedMotion ? { scale: 0.97 } : undefined}
            className={`inline-block min-h-[44px] rounded-sm px-md py-sm font-data text-[13px] uppercase tracking-[0.2em] transition-colors md:min-h-[52px] md:px-xl md:py-md md:text-[16px] lg:min-h-[60px] lg:px-2xl lg:text-[18px] ${
              playbackComplete && result
                ? 'bg-sodium text-sharpie shadow-sodium hover:bg-sodiumGlow'
                : 'cursor-not-allowed bg-stucco2 text-graphite'
            }`}
          >
            {healthAfter <= 0 ? 'See results ›' : 'Next turn ›'}
          </motion.button>
        </div>
      </section>

      {/* Block report drawer — slides in from right with backdrop. Shows
          the 3 silent AI-vs-AI pairings for the round. */}
      {reportOpen && (
        <div
          className="fixed inset-0 z-30 flex justify-end bg-asphalt/70 backdrop-blur-sm"
          onClick={() => setReportOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="pt-safe pb-safe flex h-full w-full max-w-md flex-col gap-md border-l bg-asphalt p-md md:p-lg"
            style={{ borderColor: EDGES.mid }}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-data text-[14px] uppercase tracking-[0.3em] text-bone md:text-[16px]">
                Block report · round {roundNumber}
              </h2>
              <button
                onClick={() => setReportOpen(false)}
                aria-label="Close block report"
                className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite hover:text-marker"
              >
                close ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <OtherFightsPanel round={roundNumber} />
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function SpeedControls({
  speed,
  onChange,
}: {
  speed: Speed;
  onChange: (s: Speed) => void;
}) {
  const options: Speed[] = [1, 2, 4];
  return (
    <div className="flex items-center gap-xs">
      <span className="font-data text-[10px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
        speed
      </span>
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`min-h-[44px] min-w-[44px] rounded-sm font-data text-[12px] uppercase tracking-[0.2em] transition-colors md:text-[14px] ${
            speed === opt
              ? 'bg-sodium text-sharpie'
              : 'bg-stucco2 text-bone hover:bg-stucco3'
          }`}
        >
          {opt}×
        </button>
      ))}
    </div>
  );
}
